import {
  MarketingShell,
  Seo,
  Container,
  Section,
  PageHero,
  SectionIntro,
  FeatureRow,
  StatBand,
  Steps,
  Accordion,
  CTASection,
  Reveal,
  Mock,
} from './kit.jsx'

const STEPS = [
  {
    title: 'Sign in — your market is already loaded',
    body: 'Create an account with Cognito and your workspace opens on all 10,942 verified companies — 10,942 companies across the verticals you sell into, organized by industry and region. No importing, no scraping, no data project.',
  },
  {
    title: 'Filter to your best-fit accounts',
    body: 'Slice the database by region, industry, company size, established year, and annual volume until you are looking only at the companies worth a rep’s time. Save the view and scope it to a territory.',
  },
  {
    title: 'Build an outreach sequence',
    body: 'Drop the accounts you selected into a multi-step email cadence — intro, waits, follow-ups, and a break-up — using the Apollo-style sequence builder. Write once and it runs for every company in the list.',
  },
  {
    title: 'Connect Gmail and send as yourself',
    body: 'Link your own Gmail in one step. Every email goes out from your real address, and replies land back in your inbox and get logged against the account automatically — no shared blast domain, no spam trap.',
  },
  {
    title: 'Work the pipeline',
    body: 'As replies come in, move companies from new to contacted to quoted to won on a people-level Deal Flow board. Activity and Data views roll the numbers up so you always know which regions are producing.',
  },
]

const STATS = [
  { value: '1 afternoon', label: 'From sign-up to first sequence' },
  { value: '10,942', label: 'Leads ready on day one' },
  { value: '0', label: 'Lists to buy or import' },
  { value: '50', label: 'U.S. regions covered' },
]

const FAQ = [
  {
    q: 'How long does it take to get started?',
    a: 'Most teams are sending their first sequence the same afternoon they sign up. There is no data import, no onboarding project, and no integration to stand up — you sign in, the full market is already there, you filter, build a cadence, connect Gmail, and go.',
  },
  {
    q: 'Do I need to bring my own data?',
    a: 'No. Revailo Sales ships with a complete database of 10,942 verified companies — with contacts, service area, size, established year, and annual volume — cleaned and organized by industry and region. You can start working accounts before you have added a single row of your own.',
  },
  {
    q: 'Can my whole team use it?',
    a: 'Yes. Invite reps, assign them regions, and everyone works the same source of truth with role-based collaboration. Sequences, pipeline stages, and activity are shared, so a lead handed off between reps carries its full history with it.',
  },
  {
    q: 'Does outreach really send from my own Gmail?',
    a: 'Yes. You connect your Gmail account once, and every email sends from your real address with replies returning to your inbox. Nothing routes through a shared sending domain, which is why reply rates hold up.',
  },
]

export default function HowItWorksPage() {
  return (
    <MarketingShell active="">
      <Seo
        title="How it works"
        description="See how Revailo Sales takes B2B sales teams from sign-in to closed deal — 10,942 verified leads pre-loaded, filtering, multi-step Gmail outreach, and pipeline in one workspace."
        path="/how-it-works"
        keywords="how it works, B2B lead engine, cold outreach, Gmail sequences, sales pipeline"
      />

      <PageHero
        eyebrow="How it works"
        title={
          <>
            From sign-in to closed deal, <br className="max-sm:hidden" />
            without the busywork in between.
          </>
        }
        lead="No data project and no long onboarding. Sign in and your entire market is already loaded — 10,942 verified companies, ready to filter, sequence, and work the same day."
        primary={{ label: 'Start free', to: '/signup' }}
        secondary={{ label: 'Book a demo', to: '/demo' }}
      />

      <Section>
        <Container>
          <Reveal>
            <SectionIntro
              eyebrow="The five steps"
              title="Everything happens in one workspace"
              lead="Sourcing, filtering, outreach, and pipeline live together — so a lead never leaves the tool between finding it and closing it."
            />
          </Reveal>
          <div className="mt-14" style={{ maxWidth: 820, margin: '56px auto 0' }}>
            <Reveal>
              <Steps items={STEPS} />
            </Reveal>
          </div>
        </Container>
      </Section>

      <Section className="py-14">
        <Container>
          <Reveal>
            <StatBand stats={STATS} />
          </Reveal>
        </Container>
      </Section>

      <Section>
        <Container>
          <div className="flex flex-col" style={{ gap: 96 }}>
            <FeatureRow
              eyebrow="Step 2 — Filter"
              title="Find your best-fit accounts in seconds"
              body="The whole database is already sortable and searchable. Narrow 10,942 companies down to the ones that match your industry, territory, and deal size, then hand each rep exactly the slice they should be working."
              bullets={[
                'Filter by region, industry, size, established year, and volume',
                'Real named contacts and service areas on every row',
                'Save a view and scope it to a rep’s territory',
              ]}
              media={
                <Reveal>
                  <Mock kind="leads" />
                </Reveal>
              }
            />
            <FeatureRow
              reverse
              eyebrow="Steps 3 & 4 — Sequence and send"
              title="Multi-step cadences from your own inbox"
              body="Build the sequence once — intro email, waits, follow-ups, and a break-up — then connect Gmail and send as yourself. Replies come straight back to your inbox and get logged against the account, so nothing slips."
              bullets={[
                'Apollo-style builder with steps, waits, and branches',
                'Send and receive through your real Gmail account',
                'Every send and reply tracked on the account timeline',
              ]}
              media={
                <Reveal>
                  <Mock kind="sequence" />
                </Reveal>
              }
            />
          </div>
        </Container>
      </Section>

      <Section className="py-14">
        <Container>
          <Reveal>
            <SectionIntro
              eyebrow="Questions"
              title="Getting started, answered"
              lead="The details teams ask about before they sign up."
            />
          </Reveal>
          <div className="mt-10" style={{ maxWidth: 760, margin: '40px auto 0' }}>
            <Reveal>
              <Accordion items={FAQ} />
            </Reveal>
          </div>
        </Container>
      </Section>

      <CTASection
        title="Your market is loaded and waiting."
        body="Start free and go from sign-in to your first Gmail sequence this afternoon. No credit card, no data project."
        primary={{ label: 'Start free', to: '/signup' }}
        secondary={{ label: 'Talk to sales', to: '/contact' }}
      />
    </MarketingShell>
  )
}
